import * as React from 'react'
import { useService } from '@halo/hooks/use-service'
import { AppService, DomainService, FeedService, SegmentService } from '@halo/services/index'
import {
    calculateArticleAgeInputValue,
    calculatePreScheduleInputValue,
    FeedDataForm,
    generateNewFeedModel,
    getCurrentValues,
} from '@pushly/aqe/lib/components/feeds/feed-data-form'
import { feedFormReducer, IFeedDataFormState } from '@pushly/aqe/lib/components/feeds/reducer'
import { RouteComponentProps } from 'react-router-dom'
import { FdfFormMode } from '@pushly/aqe/lib/enums/fdf-form-mode.enum'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { hydrateDomainModel } from '@halo/_utils/models-hydration'
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from 'react'
import { DomainDto } from '@halo/dtos/domain.dto'
import { SegmentDto } from '@halo/dtos/segment.dto'
import { useMountedRef } from '@halo/hooks/use-mounted-ref'
import { FeedType } from '@pushly/models/lib/enums/feed-type'
import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { FixedFeedSchedule } from '@pushly/models/lib/structs/feeds/fixed-feed-schedule'
import { FeedSendStrategy } from '@pushly/aqe/lib/enums/index'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { calculateRelativeDate, getDefaultNotificationLifespanInputValue } from '@pushly/aqe/lib/components/feeds/utils'
import { enumFromValue } from '@pushly/models'
import { getEnabledDeliveryChannels } from '@halo/_utils/domain'
import { Mutable } from '@pushly/models/lib/types/mutable'
import { LoadingScreen } from '@halo/components/loading-screen/loading-screen'
import { noop } from '@pushly/aqe/lib/utils/noop'
import { PageHeader } from '@pushly/aqe/lib/components/page-header/page-header'
import { isAsapFeedSchedule } from '@pushly/models/lib/structs'
import { AppState } from '@halo/stores/app.state'
import { Crumb, makeCrumbClick } from '@halo/_utils/breadcrumb'

interface IFeedDataFormProps
    extends RouteComponentProps<
        any,
        any,
        {
            domain?: DomainDto
            feed: Feed
            mode?: FdfFormMode
        }
    > {}

const DOMAINS_ROUTE = '/domains'

export function FeedDataFormWrapper(props: IFeedDataFormProps) {
    const appService = useService(AppService)
    const appState = useService(AppState)
    const domainService = useService(DomainService)
    const segmentService = useService(SegmentService)
    const feedService = useService(FeedService)

    const mode = props.location.state?.mode ?? (/edit$/.test(props.match.url) ? FdfFormMode.EDIT : FdfFormMode.CREATE)
    const domain = props.location.state?.domain ? hydrateDomainModel(props.location.state?.domain) : undefined
    const domainId = domain?.id ?? parseInt(props.match.params.domainId, 10)
    const originalFeed = props.location.state?.feed ? Feed.from(props.location.state.feed) : undefined!
    const feedId = originalFeed?.id ?? parseInt(props.match.params.feedId, 10)

    // Initialize state with useReducer
    const initialState: IFeedDataFormState = {
        loadingFeed: false,
        loadingDomain: false,
        loadingSegments: false,
        loadingReach: false,
        loadingPreview: false,
        loadingPrimaryResources: true,

        originalFeed,
        workingModel: originalFeed?.clone(),
        domain,
        domainSegments: [],
        domainEnabledChannels: [],
        currReach: undefined,
        previewNotifications: [],
        estimatedPreviewDeliveries: [],

        previewIndex: 0,
        isInitialPreviewLoad: true,
        hasReachEstimate: false,

        articleAgeInputValue: originalFeed ? calculateArticleAgeInputValue(originalFeed.config.articleMaxAge) : 0,
        preScheduleInputValue: originalFeed
            ? calculatePreScheduleInputValue((originalFeed.config.schedule as FixedFeedSchedule).deliveryDelay)
            : undefined,
        isDomainDefaultNotificationLifespan: originalFeed ? !originalFeed.config.notificationLifespan : true,
        notificationLifespanInputValue:
            originalFeed && domain
                ? getDefaultNotificationLifespanInputValue(originalFeed, hydrateDomainModel(domain))
                : undefined,
        excludedSegmentIds: originalFeed
            ? originalFeed.excludedSegmentIds?.length
                ? originalFeed.excludedSegmentIds
                : undefined
            : undefined,
    }

    const [state, dispatch] = useReducer(feedFormReducer, initialState)

    const audienceChanged = useRef<boolean>(false)
    const [_mounted, runIfMounted] = useMountedRef()

    // shared actions
    const setWorkingModel = (updated: Mutable<Feed>) => dispatch({ type: 'SET_WORKING_MODEL', payload: updated })
    const setPreviewIndex = (updated: number) => dispatch({ type: 'SET_PREVIEW_INDEX', payload: updated })
    const setEstimatedPreviewDeliveries = (updated: any[]) =>
        dispatch({ type: 'SET_ESTIMATED_PREVIEW_DELIVERIES', payload: updated })

    useLayoutEffect(() => {
        if (!state.domain) {
            fetchDomain()
        }

        if (!state.domainSegments || state.domainSegments.length === 0) {
            loadDomainSegments()
        }
    }, [state.domain?.id, feedId])

    useLayoutEffect(() => {
        /**
         * If page was accessed directly with no feed state passed during
         * navigation we will load feed from remote end.
         */
        const loadFeedModel = async () => {
            if (
                !state.loadingFeed &&
                !state.workingModel &&
                state.domainSegments &&
                state.domainSegments.length &&
                state.domainEnabledChannels.length
            ) {
                dispatch({ type: 'SET_LOADING_FEED', payload: true })

                let feed: Feed = new Feed()
                if (!feedId) {
                    feed = generateNewFeedModel(domainId, state.domainEnabledChannels)
                } else {
                    const { ok, data } = await feedService.fetchFeedById(domainId, feedId, {
                        showLoadingScreen: true,
                    })

                    if (ok) {
                        feed = Feed.from(data)
                    }
                }

                if (feed) {
                    const updated = MutableFeed.from(feed)
                    if (!feed.config.channels) {
                        updated.config.channels = state.domainEnabledChannels
                    }

                    dispatch({ type: 'INITIALIZE_FROM_FEED', payload: { feed, domain: state.domain } })
                    const defaultSegment = state.domainSegments?.find((ds) => ds.is_default)

                    if (defaultSegment) {
                        checkAndSetIfFeedTargetsAll(updated, defaultSegment)
                    }
                }

                dispatch({ type: 'SET_LOADING_FEED', payload: false })
            }
        }

        loadFeedModel()
    }, [state.domainSegments, state.domain, state.domainEnabledChannels])

    useEffect(() => {
        const fetchReach = async () => {
            if (state.workingModel) {
                dispatch({ type: 'SET_LOADING_REACH', payload: true })

                const res = await segmentService.estimateSegmentReach(
                    domainId,
                    state.workingModel.segmentIds ?? [],
                    state.workingModel.excludedSegmentIds ?? [],
                    state.workingModel.config.channels,
                    'count',
                    ['NOTIFICATION'],
                    {
                        cancellationKey: 'feed-fetch-reach',
                    },
                )

                if (!res.cancelled) {
                    runIfMounted(() => {
                        dispatch({ type: 'SET_CURR_REACH', payload: res.data })
                        dispatch({ type: 'SET_LOADING_REACH', payload: false })
                    })
                }
            }
        }

        fetchReach()
    }, [
        domainId,
        JSON.stringify(state.workingModel?.segmentIds),
        JSON.stringify(state.workingModel?.excludedSegmentIds),
        JSON.stringify(state.workingModel?.config.channels),
    ])

    useEffect(() => {
        const fetchNotificationPreview = async () => {
            if (state.workingModel) {
                if (state.isInitialPreviewLoad && !state.workingModel.url) {
                    dispatch({ type: 'SET_IS_INITIAL_PREVIEW_LOAD', payload: false })
                    return
                }

                if (!isValidPreviewRequest()) {
                    if (state.workingModel.url && state.previewNotifications.length === 0) {
                        dispatch({ type: 'SET_PREVIEW_NOTIFICATIONS', payload: [] })
                    }
                    setEstimatedPreviewDeliveries([])
                }

                if (isValidPreviewRequest()) {
                    if (state.previewNotifications.length === 0 || !state.loadingPreview) {
                        setPreviewIndex(0)
                        dispatch({ type: 'SET_LOADING_PREVIEW', payload: true })
                    }

                    const res =
                        state.workingModel.url &&
                        (await feedService.fetchFeedPreview(
                            domainId,
                            state.workingModel.config,
                            state.workingModel.url,
                            state.workingModel.feedType,
                            5,
                            { cancellationKey: 'feeds-initial-v4-preview-fetch' },
                        ))

                    if (!res.ok) {
                        dispatch({ type: 'SET_PREVIEW_NOTIFICATIONS', payload: [] })
                        setEstimatedPreviewDeliveries([])
                        if (!res.cancelled) {
                            dispatch({ type: 'SET_LOADING_PREVIEW', payload: false })
                        }
                        return
                    }

                    res.data?.notifications &&
                        dispatch({ type: 'SET_PREVIEW_NOTIFICATIONS', payload: [...res.data.notifications] })
                    res.data?.estimated_delivery_times &&
                        dispatch({
                            type: 'SET_ESTIMATED_PREVIEW_DELIVERIES',
                            payload: [...res.data.estimated_delivery_times.slice(0, 7)],
                        })
                    dispatch({ type: 'SET_LOADING_PREVIEW', payload: false })
                }
            }
        }

        fetchNotificationPreview()
    }, [domainId, JSON.stringify(state.workingModel?.config), state.workingModel?.url, state.workingModel?.feedType])

    useEffect(() => {
        // reset notification lifespan input value from remote loaded state or domain default
        if (state.workingModel?.config.notificationLifespan) {
            dispatch({ type: 'SET_IS_DEFAULT_NOTIFICATION_LIFESPAN', payload: false })
            const notificationLifespan = calculateRelativeDate(
                state.workingModel.config.notificationLifespan.intervalSeconds,
                state.workingModel.config.notificationLifespan.displayMetric,
            )
            dispatch({ type: 'SET_NOTIFICATION_LIFESPAN_INPUT_VALUE', payload: notificationLifespan })
        } else if (state.domain?.defaultNotificationTtlSeconds) {
            dispatch({ type: 'SET_IS_DEFAULT_NOTIFICATION_LIFESPAN', payload: true })
            const domainDefaultNotificationLifespan = calculateRelativeDate(
                state.domain.defaultNotificationTtlSeconds,
                enumFromValue(RelativeDateDisplayMetric, state.domain.displayMeta.default_notification_ttl_metric),
            )
            dispatch({ type: 'SET_NOTIFICATION_LIFESPAN_INPUT_VALUE', payload: domainDefaultNotificationLifespan })
        }

        if (!state.domainEnabledChannels.length && state.domain) {
            const activeDomainChannels = getEnabledDeliveryChannels(state.domain, true)
            dispatch({ type: 'SET_DOMAIN_ENABLED_CHANNELS', payload: activeDomainChannels })
        }
    }, [state.loadingPrimaryResources, state.workingModel, state.domain])

    const checkAndSetIfFeedTargetsAll = useCallback((model: Feed, defaultSegment: SegmentDto) => {
        if (!audienceChanged.current) {
            if (model.isAll && defaultSegment) {
                const updated = model.toMutable()
                updated.segmentIds = [defaultSegment.id!]
                setWorkingModel(updated)
            } else if (model.segmentIds?.[0]?.toString() === defaultSegment.id!.toString()) {
                const updated = model.toMutable()
                updated.isAll = true
                setWorkingModel(updated)
            }
        }
    }, [])

    async function fetchDomain() {
        if (!state.domain) {
            dispatch({ type: 'SET_LOADING_DOMAIN', payload: true })

            const { ok, data } = await domainService.fetchById(domainId)

            if (ok) {
                const hydratedDomain = hydrateDomainModel(data)
                dispatch({ type: 'SET_DOMAIN', payload: hydratedDomain })
                dispatch({
                    type: 'SET_NOTIFICATION_LIFESPAN_INPUT_VALUE',
                    payload: getDefaultNotificationLifespanInputValue(state.workingModel, hydratedDomain),
                })

                if (!feedId && state.workingModel) {
                    const updated = state.workingModel.toMutable()
                    updated.config.channels = state.domainEnabledChannels

                    setWorkingModel(updated)
                }
            }

            dispatch({ type: 'SET_LOADING_DOMAIN', payload: false })
        }
    }

    async function loadDomainSegments() {
        dispatch({ type: 'SET_LOADING_SEGMENTS', payload: true })

        const { ok, data } = await segmentService.fetchSegmentsByDomainId(domainId, {
            query: {
                includeTreated: 0,
                pagination: 0,
                fields: 'id,name,source,computedStatus,isDefault,iconUrl',
                source: 'standard',
            },
        })

        if (ok) {
            dispatch({ type: 'SET_DOMAIN_SEGMENTS', payload: data })

            const defaultSegment = data?.find((ds) => ds.is_default)
            if (state.workingModel && defaultSegment) {
                checkAndSetIfFeedTargetsAll(state.workingModel, defaultSegment)
            }
        }

        dispatch({ type: 'SET_LOADING_SEGMENTS', payload: false })
    }

    async function save() {
        if (isValidFeed()) {
            const feedDto = state.workingModel.serialize()
            const { ok, data: feed } =
                mode !== FdfFormMode.EDIT
                    ? await feedService.create(domainId, feedDto, {
                          showLoadingScreen: true,
                      })
                    : await feedService.patchFeed(
                          domainId,
                          state.workingModel.id,
                          getCurrentValues(state.workingModel, state.originalFeed),
                          {
                              showLoadingScreen: true,
                          },
                      )

            if (ok) {
                appService.route(`/domains/${domainId}/feeds`)
            }
        }
    }

    const handleBreadcrumbClick = useCallback(makeCrumbClick(appService), [])

    const isValidFeed = useCallback((): boolean => {
        let validFeed = true
        if (!state.workingModel.name) {
            validFeed = false
            simpleNotification('error', 'Name is required')
        }

        if (!state.workingModel.url) {
            validFeed = false
            simpleNotification('error', 'URL is required')
        }

        if (!state.workingModel.config.schedule.startTime) {
            validFeed = false
            simpleNotification('error', 'Start Time is required')
        }

        if (isAsapFeedSchedule(state.workingModel.config.schedule)) {
            if (!state.workingModel.config.schedule.endTime) {
                validFeed = false
                simpleNotification('error', 'End Time is required')
            }

            if (!state.workingModel.config.schedule.minFrequency?.intervalSeconds) {
                validFeed = false
                simpleNotification('error', 'Send Frequency is required')
            }
        }

        if (state.workingModel.feedType === FeedType.JSON) {
            if (!state.workingModel.config.feedJsonpath?.articles) {
                validFeed = false
                simpleNotification('error', 'JSONPath Articles is required')
            }

            if (!state.workingModel.config.feedJsonpath?.properties.url) {
                validFeed = false
                simpleNotification('error', 'JSONPath URL is required')
            }

            if (!state.workingModel.config.feedJsonpath?.properties.title) {
                validFeed = false
                simpleNotification('error', 'JSONPath Title is required')
            }
        }

        if (state.workingModel.feedType === FeedType.XML) {
            if (!state.workingModel.config.feedXpath?.articles) {
                validFeed = false
                simpleNotification('error', 'XPath Articles is required')
            }

            if (!state.workingModel.config.feedXpath?.properties.url) {
                validFeed = false
                simpleNotification('error', 'XPath URL is required')
            }

            if (!state.workingModel.config.feedXpath?.properties.title) {
                validFeed = false
                simpleNotification('error', 'XPath Title is required')
            }
        }

        return validFeed
    }, [
        state.workingModel?.name,
        state.workingModel?.url,
        JSON.stringify(state.workingModel?.config?.schedule),
        JSON.stringify(state.workingModel?.config?.feedJsonpath),
        JSON.stringify(state.workingModel?.config?.feedXpath),
    ])

    const isValidPreviewRequest = useCallback((): boolean => {
        let validFeedPreview = true

        if (!state.workingModel?.url || !state.workingModel?.config?.schedule?.startTime) {
            validFeedPreview = false
        }

        return validFeedPreview
    }, [state.workingModel?.url, state.workingModel?.config?.schedule?.startTime])

    const cancelHandler = () => {
        !props.history?.location?.state ? appService.route(`/domains/${domainId}/feeds`) : appService.routeBack()
    }

    const crumbs: Crumb[] | undefined = state.domain
        ? [
              { title: 'Domains', href: DOMAINS_ROUTE, onClick: handleBreadcrumbClick },
              {
                  title: state.domain.displayName,
                  href: `${DOMAINS_ROUTE}/${state.domain.id}`,
                  onClick: handleBreadcrumbClick,
              },
              {
                  title: 'Feeds',
                  href: `${DOMAINS_ROUTE}/${state.domain.id}/feeds`,
                  onClick: handleBreadcrumbClick,
              },
              { title: state.workingModel?.id ? `ID: ${state.workingModel.id}` : 'Create' },
          ]
        : undefined

    return state.loadingPrimaryResources ? (
        <LoadingScreen loading={true} />
    ) : (
        <div>
            <PageHeader
                title={mode !== FdfFormMode.EDIT ? 'Create Feed' : 'Edit Feed'}
                onTitleSet={noop}
                breadcrumb={crumbs}
            />
            <FeedDataForm
                state={state}
                dispatch={dispatch}
                mode={mode}
                setWorkingModel={setWorkingModel}
                setPreviewIndex={setPreviewIndex}
                setEstimatedPreviewDeliveries={setEstimatedPreviewDeliveries}
                onAudienceChanged={audienceChanged}
                onSave={save}
                onCancel={cancelHandler}
                userId={appState.currentUser.id}
            />
        </div>
    )
}
